<template>
    <div class="p-2 flex flex-col w-full h-full gap-2">
        <div class="text-2xl">Konsernavstemming</div>
        <table v-if="crossTenantAccounts.length">
            <tbody>
                <tr>
                    <th>Lokal konto</th>
                    <th>Foretak</th>
                    <th>Konto</th>
                    <th>Beskrivelse</th>
                </tr>
                <tr
                    v-for="accountLink in crossTenantAccounts"
                    :key="accountLink.localAccountNo"
                    class="hover:text-primary cursor-pointer"
                    @click="onCrossTenantAccountLinkClicked(accountLink)"
                >
                    <td>{{ accountLink.localAccountNo }}</td>
                    <td>{{ getRemoteTenantName(accountLink.remoteTenantId) }}</td>
                    <td>{{ accountLink.remoteAccountNo }}</td>
                    <td>{{ accountLink.description }}</td>
                </tr>
            </tbody>
        </table>
        <date-range-selector v-model="dateRange" class="w-fit"/>
        <UCard class="flex w-full grow justify-evenly" :ui="{body: 'grow flex'}">
            <UContainer>
                <div class="text-lg">{{ selectedOrg?.organizationName }}</div>
                <account-picker
                    v-model:account-no="localAccount"
                    v-model:sub-account-uuid="localSubAccount"
                    class="mt-6"
                    :accounts="[...bookkeepingAccounts, ...localCustomers, ...localSuppliers]"
                    single-pane
                    :single-pane-show-actions="false"
                    placeholder="Velg konto"
                    @update:account-no="fetchLocalLedger"
                    @update:sub-account-uuid="fetchLocalSubLedger"
                />
                <div v-if="localAccount" class="flex items-baseline justify-between my-6">
                    <div>Balanse</div>
                    <div class="text-xl text-primary">{{ formatCurrency(localBalance) }}</div>
                </div>
                <gen-leg-account-grid
                    v-if="localAccount && localLedger && isAccountLedger(localLedger)"
                    :key="localAccount"
                    :account-no="localAccount"
                    :ledger="localLedger as LedgerAccount"
                    :query="localSearchParams"
                />
            </UContainer>
            <USeparator orientation="vertical"/>
            <UContainer>
                <vue-multiselect
                    v-model="computedRemote"
                    :options="remoteOrgs"
                    label="organizationName"
                    placeholder="Velg foretak"
                />
                <div v-if="remoteTenant">
                    <account-picker
                        v-model:account-no="remoteAccount"
                        v-model:sub-account-uuid="remoteSubAccount"
                        class="mt-3"
                        single-pane
                        :single-pane-show-actions="false"
                        placeholder="Velg konto"
                        :accounts="[...remoteAccounts, ...remoteCustomers, ...remoteSuppliers]"
                        @update:account-no="fetchRemoteLedger"
                        @update:sub-account-uuid="fetchRemoteSubLedger"
                    />
                </div>
                <div v-if="remoteAccount" class="flex items-baseline justify-between my-6">
                    <div>Balanse</div>
                    <div class="text-xl text-primary">{{ formatCurrency(remoteBalance) }}</div>
                </div>
                <gen-leg-account-grid
                    v-if="remoteAccount && remoteLedger && isAccountLedger(remoteLedger)"
                    :account-no="remoteAccount"
                    :ledger="remoteLedger as LedgerAccount"
                    :query="remoteSearchParams"
                    :override-tenant-id="remoteTenant?.organizationId"
                />
            </UContainer>
        </UCard>
        <UFieldGroup v-if="localAccount && remoteTenant && remoteAccount">
            <UInput v-model="description" placeholder="Beskrivelse"/>
            <UButton @click="saveCrossTenantAccount(description)">Lagre link</UButton>
        </UFieldGroup>
    </div>
</template>

<script setup lang="ts">

import {selectedOrg} from '@/tenants/org.service'
import VueMultiselect from 'vue-multiselect'
import AccountPicker from '@/account-picker/AccountPicker.vue'
import {bookkeepingAccounts} from '@/bookkeeping-account/bookkeepingAccount.service'
import DateRangeSelector from '@/dateUtils/DateRangeSelector.vue'
import {formatCurrency} from '@/services/formatter.service'
import useCrossTenantBalance from '@/cross-tenant/cross-tenant-balance.service'
import GenLegAccountGrid from '@/genleg/GenLegAccountGrid.vue'
import {LedgerAccount} from '@/types/reports/LedgerAccount'
import {onMounted, ref, nextTick} from 'vue'
import {CrossTenantAccountLink} from '@/cross-tenant/CrossTenantAccountLink'

const {
    dateRange,
    localAccount,
    localSubAccount,
    localCustomers,
    localSuppliers,
    computedRemote,
    localLedger,
    crossTenantAccounts,
    fetchLocalLedger,
    fetchLocalSubLedger,
    isAccountLedger,
    localBalance,
    remoteOrgs,
    remoteTenant,
    remoteAccount,
    remoteSubAccount,
    remoteAccounts,
    remoteCustomers,
    remoteSuppliers,
    remoteLedger,
    localSearchParams,
    remoteSearchParams,
    fetchRemoteLedger,
    fetchRemoteSubLedger,
    remoteBalance,
    fetchCrossTenantAccounts,
    saveCrossTenantAccount,
    getRemoteTenantName
} = useCrossTenantBalance()

const description = ref('')

onMounted(fetchCrossTenantAccounts)

const onCrossTenantAccountLinkClicked = (accountLink: CrossTenantAccountLink) => {
    localAccount.value = accountLink.localAccountNo
    remoteAccount.value = accountLink.remoteAccountNo
    const remoteOrg = remoteOrgs.value.find(org => org.organizationId === accountLink.remoteTenantId)
    if (remoteOrg) {
        computedRemote.value = remoteOrg
    }
    nextTick(() => {
        fetchLocalLedger(accountLink.localAccountNo)
        fetchRemoteLedger(accountLink.remoteAccountNo)
    })
}

</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

table {
    th {
        @apply font-normal text-left px-1;
    }

    td {
        @apply px-1;
    }
}

</style>